import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { useLoader } from '@/context/LoaderContext'
import { cn } from '@/lib/utils'

interface LoaderOverlayProps {
	className?: string
	size?: number | string
}

export function LoaderOverlay({ className, size = 64 }: LoaderOverlayProps) {
	const { isLoading } = useLoader()
	const overlayRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!isLoading) {
			return
		}

		const overlay = overlayRef.current
		const previousHtmlOverflow = document.documentElement.style.overflow
		const previousBodyOverflow = document.body.style.overflow
		const previousBodyTouchAction = document.body.style.touchAction
		const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
		const blockedElements = Array.from(document.body.children)
			.filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlay)
			.map((element) => ({
				element,
				inert: element.inert,
				ariaHidden: element.getAttribute('aria-hidden'),
			}))
		const blockInteraction = (event: Event) => {
			if (overlay?.contains(event.target as Node)) return

			// Inert doesn't always prevent pointer/touch handlers from firing (e.g. some modal libs).
			// Capture listeners guarantee the interaction can't reach underlying UI.
			event.preventDefault()
			event.stopPropagation()

			if (event.type === 'focusin') overlay?.focus()
		}

		document.documentElement.style.overflow = 'hidden'
		document.body.style.overflow = 'hidden'
		document.body.style.touchAction = 'none'
		blockedElements.forEach(({ element }) => {
			element.inert = true
			element.setAttribute('aria-hidden', 'true')
		})

		const blockedEventTypes: Array<'pointerdown' | 'touchstart' | 'wheel' | 'focusin'> = ['pointerdown', 'touchstart', 'wheel', 'focusin']

		blockedEventTypes.forEach((eventType) => {
			window.addEventListener(eventType, blockInteraction, { capture: true, passive: false })
		})
		overlay?.focus()

		return () => {
			document.documentElement.style.overflow = previousHtmlOverflow
			document.body.style.overflow = previousBodyOverflow
			document.body.style.touchAction = previousBodyTouchAction
			blockedElements.forEach(({ element, inert, ariaHidden }) => {
				element.inert = inert
				if (ariaHidden === null) {
					element.removeAttribute('aria-hidden')
				} else {
					element.setAttribute('aria-hidden', ariaHidden)
				}
			})

			blockedEventTypes.forEach((eventType) => {
				window.removeEventListener(eventType, blockInteraction, { capture: true })
			})
			previouslyFocused?.focus()
		}
	}, [isLoading])

	if (!isLoading) {
		return null
	}

	return createPortal(
		<div
			ref={overlayRef}
			className={cn('fixed inset-0 z-[2147483647] flex cursor-wait touch-none items-center justify-center overscroll-none bg-black/35 backdrop-blur-[2px]', className)}
			role='dialog'
			aria-modal='true'
			aria-label='Loading'
			tabIndex={-1}
		>
			<BetsCycleLoader size={size} />
		</div>,
		document.body,
	)
}
